import { z } from 'zod';
import {
  AuditSchema,
  AppointmentIdSchema,
  InstantSchema,
  OrderIdSchema,
  PartyIdSchema,
  TenantScopedSchema,
  ThreadIdSchema,
  UserIdSchema,
} from './primitives.js';
import { ChannelKindSchema } from './parties.js';

/* ------------------------------------------------------------------ *
 * Messaging
 *
 * The brief asks for one place to reach a contact across WhatsApp,
 * Telegram, email and the rest. The right model is a *thread per
 * party*, with messages tagged by transport — not an inbox per
 * channel. A salesperson wants "everything we've said to Sorlí",
 * regardless of which app it arrived in.
 *
 * Each transport keeps its own capability flags because they are not
 * interchangeable: WhatsApp has a 24-hour reply window and requires
 * approved templates outside it, Telegram does not, email has no
 * delivery receipt worth trusting. The UI reads capabilities and
 * disables the composer accordingly instead of failing on send.
 * ------------------------------------------------------------------ */

export interface ChannelCapabilities {
  attachments: boolean;
  richText: boolean;
  readReceipts: boolean;
  /** Hours after the contact's last message during which free-form replies are allowed. */
  replyWindowHours: number | null;
  /** Outside the window, only pre-approved templates may be sent. */
  requiresTemplateOutsideWindow: boolean;
  maxLength: number | null;
}

export const CHANNEL_CAPABILITIES: Record<
  z.infer<typeof ChannelKindSchema>,
  ChannelCapabilities
> = {
  whatsapp: {
    attachments: true, richText: false, readReceipts: true,
    replyWindowHours: 24, requiresTemplateOutsideWindow: true, maxLength: 4096,
  },
  telegram: {
    attachments: true, richText: true, readReceipts: false,
    replyWindowHours: null, requiresTemplateOutsideWindow: false, maxLength: 4096,
  },
  email: {
    attachments: true, richText: true, readReceipts: false,
    replyWindowHours: null, requiresTemplateOutsideWindow: false, maxLength: null,
  },
  sms: {
    attachments: false, richText: false, readReceipts: false,
    replyWindowHours: null, requiresTemplateOutsideWindow: false, maxLength: 1600,
  },
  phone: {
    attachments: false, richText: false, readReceipts: false,
    replyWindowHours: null, requiresTemplateOutsideWindow: false, maxLength: null,
  },
  signal: {
    attachments: true, richText: false, readReceipts: true,
    replyWindowHours: null, requiresTemplateOutsideWindow: false, maxLength: 4096,
  },
  instagram: {
    attachments: true, richText: false, readReceipts: true,
    replyWindowHours: 24, requiresTemplateOutsideWindow: true, maxLength: 1000,
  },
};

export const MessageDirectionSchema = z.enum(['in', 'out']);
export const MessageStatusSchema = z.enum([
  'queued', 'sent', 'delivered', 'read', 'failed',
]);

export const MessageSchema = z.object({
  id: z.string().min(1),
  threadId: ThreadIdSchema,
  channel: ChannelKindSchema,
  direction: MessageDirectionSchema,
  status: MessageStatusSchema,
  /** Null for inbound. */
  authorId: UserIdSchema.nullable().default(null),
  authorName: z.string().nullable().default(null),
  body: z.string(),
  attachments: z
    .array(z.object({ url: z.string(), name: z.string(), mime: z.string(), sizeBytes: z.number().int() }))
    .default([]),
  /** Voice notes are how half of this industry orders. Transcribe and keep both. */
  transcript: z.string().nullable().default(null),
  /** Links a message to what it is about, so the order screen can show the conversation. */
  relatedOrderId: OrderIdSchema.nullable().default(null),
  templateKey: z.string().nullable().default(null),
  sentAt: InstantSchema,
  failureReason: z.string().nullable().default(null),
});
export type Message = z.infer<typeof MessageSchema>;

export const ThreadSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: ThreadIdSchema,
  partyId: PartyIdSchema,
  partyName: z.string().min(1),
  /** Channels this thread has traffic on. The composer switches between them. */
  channels: z.array(ChannelKindSchema).min(1),
  subject: z.string().nullable().default(null),
  lastMessageAt: InstantSchema,
  lastMessagePreview: z.string().default(''),
  lastMessageChannel: ChannelKindSchema,
  unreadCount: z.number().int().min(0).default(0),
  assignedToId: UserIdSchema.nullable().default(null),
  status: z.enum(['open', 'snoozed', 'closed']).default('open'),
  snoozedUntil: InstantSchema.nullable().default(null),
  tags: z.array(z.string()).default([]),
});
export type Thread = z.infer<typeof ThreadSchema>;

/** Pre-approved outbound template, per channel and locale. */
export const MessageTemplateSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  key: z.string().min(1),
  channel: ChannelKindSchema,
  /** Body per locale. Placeholders: {{orderNumber}}, {{eta}}, {{amount}}. */
  body: z.record(z.string(), z.string()),
  variables: z.array(z.string()).default([]),
  approvedAt: InstantSchema.nullable().default(null),
});
export type MessageTemplate = z.infer<typeof MessageTemplateSchema>;

/**
 * Can we send free-form text right now, or only a template?
 * The composer calls this on every channel switch.
 */
export const canSendFreeform = (
  channel: z.infer<typeof ChannelKindSchema>,
  lastInboundAt: string | null,
  now: number,
): boolean => {
  const caps = CHANNEL_CAPABILITIES[channel];
  if (!caps.requiresTemplateOutsideWindow || caps.replyWindowHours === null) return true;
  if (!lastInboundAt) return false;
  return now - Date.parse(lastInboundAt) < caps.replyWindowHours * 3_600_000;
};

/* ------------------------------------------------------------------ *
 * Appointments
 * ------------------------------------------------------------------ */

export const AppointmentSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: AppointmentIdSchema,
  title: z.string().min(1),
  kind: z.enum([
    'sales-visit',
    'tasting',
    'farm-visit',
    'delivery-slot',
    'collection',
    'internal',
    'call',
  ]),
  partyId: PartyIdSchema.nullable().default(null),
  partyName: z.string().nullable().default(null),
  /** Team members attending. */
  attendeeIds: z.array(UserIdSchema).default([]),
  startsAt: InstantSchema,
  endsAt: InstantSchema,
  location: z.string().nullable().default(null),
  /** Set when the meeting is about a specific order. */
  relatedOrderId: OrderIdSchema.nullable().default(null),
  status: z.enum(['scheduled', 'confirmed', 'done', 'no-show', 'cancelled']).default('scheduled'),
  reminderMinutesBefore: z.number().int().min(0).nullable().default(60),
  note: z.string().nullable().default(null),
});
export type Appointment = z.infer<typeof AppointmentSchema>;
